import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { PersonalizedPracticeChartComponent } from '../personalized-practice-chart/personalized-practice-chart.component';
import {
  QaiDatepickerMaterialComponent,
  QaiTableComponent,
  type HeaderConfig,
  TableRow,
  QaiFloatingSelectOption,
  QaiFloatingSelectComponent,
  QaiInnerWrapComponent,
  QaiLoadingComponent,
} from '@qai/ui';
import { IPersonalizedPracticeSession } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-session';
import { PersonalizedPracticeService } from 'src/app/services/personalized-practice/personalized-practice.service';
import { LocaleService } from 'src/app/services/locale.service';
import { TranslationService } from 'src/app/services/translation.service';
import { AppConstants } from 'src/app/app-constants';
import { localeUpperCase } from 'src/app/utils/locale-case.util';

@Component({
  selector: 'app-personalized-practice-previous-sessions',
  imports: [
    CommonModule,
    TranslocoModule,
    PersonalizedPracticeChartComponent,
    QaiDatepickerMaterialComponent,
    QaiTableComponent,
    QaiFloatingSelectComponent,
    QaiInnerWrapComponent,
    QaiLoadingComponent,
  ],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'personalizedPractice',
    },
  ],
  templateUrl: './personalized-practice-previous-sessions.component.html',
  styleUrl: './personalized-practice-previous-sessions.component.scss',
})
export class PersonalizedPracticePreviousSessionsComponent implements OnInit {
  @Input() isActive!: boolean;
  @Input() isComplete!: boolean;

  moduleAssignmentUuid!: string;
  headers: HeaderConfig[] = [];
  tableData: TableRow[] = [];
  filteredTableRows: TableRow[] = [];
  data: IPersonalizedPracticeSession[] = [];
  filteredSessions: IPersonalizedPracticeSession[] = [];
  isLoading: boolean = true;
  displayPreviousSessions: boolean = false;

  // Date filter
  selectedDate: Date | null = null;
  currentLocale: string = 'en';

  // Conversation type
  conversationTypeOptions: QaiFloatingSelectOption[] = [];
  conversationTypeSelectedValue: string = '';
  conversationType: string[] = [];

  // Difficulty
  difficultyTypeOptions: QaiFloatingSelectOption[] = [];
  difficultyTypeSelectedValue: string = '';
  difficultyType: string[] = [];

  // Temperament
  temperamentTypeOptions: QaiFloatingSelectOption[] = [];
  temperamentTypeSelectedValue: string = '';
  temperamentType: string[] = [];

  constructor(
    private router: Router,
    private personalizedPracticeService: PersonalizedPracticeService,
    private localeService: LocaleService,
    private translationService: TranslationService
  ) {
    this.headers = [
      {
        name: this.translationService.translate(
          'SESSION_DATE',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'DURATION',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'CONVERSATION_TYPE',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'DIFFICULTY',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'TEMPERAMENT',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'OVERALL_SCORE',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'MESSAGING',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'METHODOLOGY',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'SKILLS',
          'personalizedPractice'
        ),
        sortable: true,
        clickable: false,
      },
      {
        name: this.translationService.translate(
          'RESULTS',
          'personalizedPractice'
        ),
        sortable: false,
        clickable: true,
      },
    ];
  }

  ngOnInit(): void {
    this.currentLocale = this.localeService.getCurrentLanguage() || 'en';

    // Extract UUID from URL - get the last segment after the last slash
    const urlSegments = this.router.url.split('/');
    const uuid = urlSegments[urlSegments.length - 1];
    if (uuid) {
      this.moduleAssignmentUuid = uuid;
    }

    this.loadSessions();
  }

  private loadSessions(): void {
    this.isLoading = true;
    this.personalizedPracticeService
      .getPersonalizedPracticeSessions(this.moduleAssignmentUuid)
      .subscribe({
        next: (sessions: IPersonalizedPracticeSession[]) => {
          this.data = sessions;
          if (this.data && this.data.length > 0) {
            this.tableData = this.transformDataToTableRows(this.data);
            this.filteredTableRows = this.tableData;
            this.filteredSessions = this.data;
            this.getUniqueSelectValues();
            this.displayPreviousSessions = true;
          } else {
            this.tableData = [];
            this.filteredTableRows = [];
            this.filteredSessions = [];
            // Product is Active    no sessions = Hide previous session section
            // Product is COMPLETED no sessions = Show previous session section
            if (this.isActive) {
              this.displayPreviousSessions = false;
            }
            if (this.isComplete) {
              this.displayPreviousSessions = true;
            }
          }
        },
        error: (error) => {
          console.error('Error loading personalized practice sessions:', error);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  private transformDataToTableRows(
    apiData: IPersonalizedPracticeSession[]
  ): TableRow[] {
    return apiData.map((session) => this.createSessionTableRow(session));
  }

  private formatDuration(durationInSeconds: string | number): string {
    const seconds =
      typeof durationInSeconds === 'string'
        ? parseInt(durationInSeconds, 10)
        : durationInSeconds;

    if (isNaN(seconds)) {
      return String(durationInSeconds); // Return original if not a valid number
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return this.translationService.translate(
        'DURATION_MINUTES_SECONDS',
        'personalizedPractice',
        { minutes, seconds: remainingSeconds }
      );
    } else {
      return this.translationService.translate(
        'DURATION_SECONDS',
        'personalizedPractice',
        { seconds: remainingSeconds }
      );
    }
  }

  private createSessionTableRow(
    session: IPersonalizedPracticeSession
  ): TableRow {
    const row = {
      id: session.practiceCallUuid,
      callUuid: session.callUuid,
      callType: session.callType,
      visualAidEnabled: session.visualAidEnabled,
      [this.headers[0].name]: {
        type: 'text',
        value: this.localeService.formatTableDateLong(session.scoredAt),
      },
      [this.headers[1].name]: {
        type: 'text',
        value: this.formatDuration(session.durationInSeconds),
      },
      [this.headers[2].name]: {
        type: 'text',
        value: session.callName
          ? this.capitalizeWords(session.callName, this.currentLocale)
          : '-',
      },
      [this.headers[3].name]: {
        type: 'text',
        value: session.difficulty
          ? this.capitalizeWords(session.difficulty, this.currentLocale)
          : '-',
      },
      [this.headers[4].name]: {
        type: 'text',
        value: session.temperament
          ? this.capitalizeWords(session.temperament, this.currentLocale)
          : '-',
      },
      [this.headers[5].name]: {
        type: 'text',
        value:
          session.scoreOverall != null
            ? this.localeService.formatNumber(session.scoreOverall, 1)
            : '-',
      },
      [this.headers[6].name]: {
        type: 'text',
        value:
          session.messaging != null
            ? this.localeService.formatNumber(session.messaging, 1)
            : '-',
      },
      [this.headers[7].name]: {
        type: 'text',
        value:
          session.methodology != null
            ? this.localeService.formatNumber(session.methodology, 1)
            : '-',
      },
      [this.headers[8].name]: {
        type: 'text',
        value:
          session.scoreSkills != null
            ? this.localeService.formatNumber(session.scoreSkills, 1)
            : '-',
      },
      [this.headers[9].name]: {
        type: 'text',
        value: this.translationService.translate(
          'VIEW_FEEDBACK',
          'personalizedPractice'
        ),
      },
    } as const;

    return { ...row } as unknown as TableRow;
  }

  onCellViewFeedbackClick(event: {
    rowData: TableRow;
    columnName: string;
    event: Event;
  }): void {
    if (
      event.columnName !==
      this.translationService.translate('RESULTS', 'personalizedPractice')
    ) {
      return;
    }
    const rowData = event.rowData as any;
    const practiceCallUuid = rowData.id;
    const callUuid = rowData.callUuid;
    const callType = rowData.callType;
    const visualAidEnabled = rowData.visualAidEnabled;

    if (!practiceCallUuid) {
      console.error('Practice Call UUID not found');
      return;
    }

    if (!callUuid) {
      console.error('Call UUID not found');
      return;
    }

    if (!callType) {
      console.error('Call type not found');
      return;
    }

    const isGuided = callType === AppConstants.CALL_TYPE_GUIDED;

    // Determine results path based on call type and visual aid setting
    let resultsPath: string;

    if (isGuided && visualAidEnabled) {
      resultsPath = '/results_visual_aid';
    } else if (isGuided) {
      resultsPath = '/results_guided';
    } else if (!isGuided) {
      resultsPath = '/result_personalized_practice';
    } else resultsPath = '/results_guided';

    // Build query params - guided calls only need callUuid, conversational need both
    const queryParams: any = {
      practiceCallUuid,
      callUuid,
    };

    if (!isGuided) {
      queryParams.moduleAssignmentUuid = this.moduleAssignmentUuid;
    }

    // Build and store return URL for navigation context
    const returnUrl = `${resultsPath}?${new URLSearchParams(
      queryParams
    ).toString()}`;
    localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));

    // Navigate to results page with query params
    this.router.navigate([resultsPath], { queryParams });
  }

  private getUniqueSelectValues(): void {
    // Extract unique conversation types
    this.conversationType = [
      ...new Set(
        this.data
          .map((session) => session.callName)
          .filter((callName): callName is string => callName != null)
      ),
    ].sort();
    this.conversationTypeOptions = [
      ...this.conversationType.map((type) => ({
        label: this.capitalizeWords(type, this.currentLocale),
        value: type,
      })),
    ];

    // Extract unique difficulty types
    this.difficultyType = [
      ...new Set(
        this.data
          .map((session) => session.difficulty)
          .filter((difficulty): difficulty is string => difficulty != null)
      ),
    ].sort();
    this.difficultyTypeOptions = [
      ...this.difficultyType.map((type) => ({
        label: this.capitalizeWords(type, this.currentLocale),
        value: type,
      })),
    ];

    // Extract unique temperament types
    this.temperamentType = [
      ...new Set(
        this.data
          .map((session) => session.temperament)
          .filter((temperament): temperament is string => temperament != null)
      ),
    ].sort();
    this.temperamentTypeOptions = [
      ...this.temperamentType.map((type) => ({
        label: this.capitalizeWords(type, this.currentLocale),
        value: type,
      })),
    ];
  }

  private capitalizeWords(str: string, locale: string): string {
    return str
      .split(' ')
      .map((word) => localeUpperCase(word.charAt(0), locale) + word.slice(1))
      .join(' ');
  }

  onDateChange(value: Date | null): void {
    this.selectedDate = value;
    this.applyFilters();
  }

  onConversationTypeChange(value: string | null): void {
    this.conversationTypeSelectedValue = value || '';
    this.applyFilters();
  }

  onDifficultyChange(value: string | null): void {
    this.difficultyTypeSelectedValue = value || '';
    this.applyFilters();
  }

  onTemperamentChange(value: string | null): void {
    this.temperamentTypeSelectedValue = value || '';
    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = [...this.tableData];

    // Filter by date
    if (this.selectedDate) {
      filtered = filtered.filter((row) => {
        const session = this.data.find((s) => s.practiceCallUuid === row.id);
        if (!session) return false;

        const sessionDate = new Date(session.scoredAt);

        // Compare only the date part (ignore time)
        return (
          sessionDate.getUTCFullYear() === this.selectedDate!.getFullYear() &&
          sessionDate.getUTCMonth() === this.selectedDate!.getMonth() &&
          sessionDate.getUTCDate() === this.selectedDate!.getDate()
        );
      });
    }

    // Filter by conversation type
    if (this.conversationTypeSelectedValue) {
      filtered = filtered.filter((row) => {
        const session = this.data.find((s) => s.practiceCallUuid === row.id);
        return session?.callName === this.conversationTypeSelectedValue;
      });
    }

    // Filter by difficulty
    if (this.difficultyTypeSelectedValue) {
      filtered = filtered.filter((row) => {
        const session = this.data.find((s) => s.practiceCallUuid === row.id);
        return session?.difficulty === this.difficultyTypeSelectedValue;
      });
    }

    // Filter by temperament
    if (this.temperamentTypeSelectedValue) {
      filtered = filtered.filter((row) => {
        const session = this.data.find((s) => s.practiceCallUuid === row.id);
        return session?.temperament === this.temperamentTypeSelectedValue;
      });
    }

    this.filteredTableRows = filtered;

    // Derive filtered session data from filtered table rows
    this.filteredSessions = this.filteredTableRows
      .map((row) => this.data.find((s) => s.practiceCallUuid === row.id))
      .filter(
        (session): session is IPersonalizedPracticeSession =>
          session !== undefined
      );
  }

  protected get hasFilteredSessions() {
    return this.filteredTableRows.length > 0;
  }
}
